import { priceFactory, zeroPrice } from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { repeatIndex } from "@@/utilities/repeat";
import { sum } from "@@/utilities/sum";
import { isOrderItem_V2 } from "./is-order-item-v2";
import type { OrderItem_V1 } from "./order-item-v1";
import type { OrderItem_V2 } from "./order-item-v2";

const calculateOrderItemPriceWithoutOptionals = (
    orderItem: OrderItem_V1 | OrderItem_V2,
) => {
    if (isOrderItem_V2(orderItem)) return orderItem.pricePerItem;
    const price = subtractFromPrice(
        orderItem.pricePerItem,
        orderItem.pickedOptionals.map(o =>
            sumPrices(
                repeatIndex(o.quantity.value).map(() =>
                    o.optional._type === "OPTIONAL"
                        ? o.optional.price
                        : zeroPrice,
                ),
            ),
        ),
    );
    const percentages = sum(
        orderItem.pickedOptionals.flatMap(o =>
            o.optional._type === "OPTIONAL_PERCENTAGE"
                ? o.optional.percentage
                : [],
        ),
    );

    if (percentages === 0) return price;
    const reverse = percentages / (1 + percentages);

    return subtractFromPrice(price, [
        priceFactory({
            amountIncludingVat: price.amountIncludingVat * reverse,
            vat: price.vat,
        }),
    ]);
};
const calculateOriginalOrderItemPriceWithoutOptionals = (
    orderItem: OrderItem_V1 | OrderItem_V2,
) => {
    if (isOrderItem_V2(orderItem))
        return orderItem.originalPrice ?? orderItem.price;

    return subtractFromPrice(
        orderItem.originalPrice ?? orderItem.price,
        orderItem.pickedOptionals.map(o =>
            sumPrices(
                repeatIndex(o.quantity.value).map(() =>
                    o.optional._type === "OPTIONAL"
                        ? o.optional.price
                        : zeroPrice,
                ),
            ),
        ),
    );
};

const calculateOrderItemPricePerItemWithoutOptionals = (
    orderItem: OrderItem_V1 | OrderItem_V2,
) => {
    if (isOrderItem_V2(orderItem)) return orderItem.pricePerItem;
    return subtractFromPrice(
        orderItem.pricePerItem,
        orderItem.pickedOptionals.map(o =>
            sumPrices(
                repeatIndex(o.quantity.value).map(() =>
                    o.optional._type === "OPTIONAL"
                        ? o.optional.price
                        : zeroPrice,
                ),
            ),
        ),
    );
};
const calculateOriginalOrderItemPricePerItemWithoutOptionals = (
    orderItem: OrderItem_V1 | OrderItem_V2,
) => {
    if (isOrderItem_V2(orderItem)) return orderItem.originalPricePerItem;
    return subtractFromPrice(
        orderItem.originalPricePerItem ?? orderItem.pricePerItem,
        orderItem.pickedOptionals.map(o =>
            sumPrices(
                repeatIndex(o.quantity.value).map(() =>
                    o.optional._type === "OPTIONAL"
                        ? o.optional.price
                        : zeroPrice,
                ),
            ),
        ),
    );
};

export {
    calculateOrderItemPricePerItemWithoutOptionals,
    calculateOrderItemPriceWithoutOptionals,
    calculateOriginalOrderItemPricePerItemWithoutOptionals,
    calculateOriginalOrderItemPriceWithoutOptionals,
};
